import {ALL_FOCUSABLE_ELEMENTS_SELECTOR} from '../constants/focusableElementsSelector';

/**
 * Options for making elements unfocusable.
 */
interface MakeElementsUnfocusableOptions {
  /**
   * Timeout in milliseconds after which the MutationObserver will be disconnected.
   * Defaults to 2000ms (2 seconds) when undefined.
   * Set to 0 to keep the observer active indefinitely.
   */
  observerTimeout?: number;
}

/**
 * Makes all focusable elements within a container unfocusable by setting their tabIndex to -1.
 * Uses MutationObserver to catch elements added asynchronously by other components' effects.
 *
 * This utility is particularly useful for offscreen content, hidden modals, or any scenario
 * where you need to prevent focusable elements from receiving keyboard focus while still
 * maintaining their DOM structure for validation or other purposes.
 *
 * @param container - The container element to process. If null, returns a no-op cleanup function.
 * @param options - Optional configuration for the observer timeout.
 * @returns A cleanup function that restores original tabIndex values and disconnects the observer.
 *
 * @example
 * ```tsx
 * useEffect(() => {
 *   const cleanup = makeElementsUnfocusable(containerRef.current);
 *   return cleanup;
 * }, [children]);
 * ```
 *
 * @example
 * ```tsx
 * // With custom timeout
 * useEffect(() => {
 *   const cleanup = makeElementsUnfocusable(containerRef.current, {
 *     observerTimeout: 5000, // Keep observer active for 5 seconds
 *   });
 *   return cleanup;
 * }, [children]);
 * ```
 */
export const makeElementsUnfocusable = (
  container: HTMLElement | null,
  options?: MakeElementsUnfocusableOptions
): (() => void) => {
  if (!container) {
    // Return no-op cleanup function if container is null
    return () => {
      // No-op
    };
  }

  const originalTabIndices = new Map<HTMLElement, string | null>();
  const {observerTimeout = 2000} = options ?? {};

  const processElements = () => {
    const focusableElements = container.querySelectorAll<HTMLElement>(
      ALL_FOCUSABLE_ELEMENTS_SELECTOR
    );

    focusableElements.forEach((element) => {
      // Only process elements that haven't been processed yet
      if (!originalTabIndices.has(element)) {
        originalTabIndices.set(element, element.getAttribute('tabindex'));
        element.setAttribute('tabindex', '-1');
      }
    });
  };

  // Process immediately for elements already in DOM
  processElements();

  // Use MutationObserver to catch elements added asynchronously by other components' effects
  // This is necessary because React children may render their content in useEffect hooks
  // that run after our effect, causing elements to be added to the DOM later
  const observer = new MutationObserver(() => {
    processElements();
  });

  observer.observe(container, {
    childList: true,
    subtree: true,
  });

  let disconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Disconnect observer after timeout if specified
  if (observerTimeout > 0) {
    disconnectTimeoutId = setTimeout(() => {
      observer.disconnect();
    }, observerTimeout);
  }

  // Cleanup function to restore original tabIndex values
  return () => {
    if (disconnectTimeoutId !== null) {
      clearTimeout(disconnectTimeoutId);
    }
    observer.disconnect();
    originalTabIndices.forEach((originalTabIndex, element) => {
      if (originalTabIndex === null || originalTabIndex === undefined) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', originalTabIndex);
      }
    });
    originalTabIndices.clear();
  };
};
